import React from 'react'
import { motion } from 'framer-motion'
import { 
  Wifi, 
  WifiOff, 
  Car, 
  Zap, 
  Activity,
  Signal,
  Battery,
  Clock
} from 'lucide-react'

const StatusPanel = ({
  isConnected,
  deviceStatus,
  currentDirection,
  currentSpeed,
  isMoving
}) => {
  const getConnectionIcon = () => {
    return isConnected ? (
      <Wifi className="w-4 h-4 text-success-400" />
    ) : (
      <WifiOff className="w-4 h-4 text-danger-400" />
    )
  }

  const getStatusColor = (isActive) => {
    return isActive ? 'text-success-400' : 'text-gray-400'
  }

  const formatRSSI = (rssi) => {
    if (rssi >= -50) return 'Excellent'
    if (rssi >= -60) return 'Good'
    if (rssi >= -70) return 'Fair'
    return 'Poor'
  }

  const getRSSIColor = (rssi) => {
    if (rssi >= -50) return 'text-success-400'
    if (rssi >= -60) return 'text-warning-400'
    if (rssi >= -70) return 'text-orange-400'
    return 'text-danger-400'
  }

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Device Status</h3>
          {getConnectionIcon()}
        </div>

        <div className="space-y-3">
          {/* Connection Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Status</span>
            <span className={`text-sm font-medium ${getStatusColor(isConnected)}`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          {/* Device Info */}
          {deviceStatus && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Device</span>
                <span className="text-sm font-medium text-primary-400">
                  {deviceStatus.name || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Version</span>
                <span className="text-sm font-medium text-secondary-400">
                  {deviceStatus.version || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Mode</span>
                <span className="text-sm font-medium text-secondary-400">
                  {deviceStatus.mode || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">SSID</span>
                <span className="text-sm font-medium text-secondary-400">
                  {deviceStatus.ssid || 'Unknown'}
                </span>
              </div>

              {deviceStatus.rssi && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Signal</span>
                  <div className="flex items-center space-x-2">
                    <Signal className="w-3 h-3 text-gray-400" />
                    <span className={`text-sm font-medium ${getRSSIColor(deviceStatus.rssi)}`}>
                      {formatRSSI(deviceStatus.rssi)} ({deviceStatus.rssi} dBm)
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Car Status */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Car Status</h3>
          <motion.div
            animate={{ 
              scale: isMoving ? [1, 1.2, 1] : 1,
              rotate: isMoving ? [0, 5, -5, 0] : 0
            }}
            transition={{ duration: 0.5, repeat: isMoving ? Infinity : 0 }}
          >
            <Car className="w-5 h-5 text-primary-400" />
          </motion.div>
        </div>

        <div className="space-y-3">
          {/* Direction */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Direction</span>
            <span className="text-sm font-medium text-primary-400">
              {currentDirection || 'STOP'}
            </span>
          </div>

          {/* Speed */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Speed</span>
            <div className="flex items-center space-x-2">
              <Zap className="w-3 h-3 text-warning-400" />
              <span className="text-sm font-medium text-warning-400">
                {currentSpeed || 0} / 1023
              </span>
            </div>
          </div>

          {/* Movement Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Movement</span>
            <div className="flex items-center space-x-2">
              <Activity className={`w-3 h-3 ${getStatusColor(isMoving)}`} />
              <span className={`text-sm font-medium ${getStatusColor(isMoving)}`}>
                {isMoving ? 'Moving' : 'Stopped'}
              </span>
            </div>
          </div>

          {/* Speed Percentage */}
          {currentSpeed > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Speed %</span>
              <span className="text-sm font-medium text-success-400">
                {Math.round((currentSpeed / 1023) * 100)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pin States */}
      {deviceStatus?.pinStates && (
        <div className="card p-4">
          <h3 className="text-lg font-semibold mb-4">Pin States</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(deviceStatus.pinStates).map(([pin, state]) => (
              <div key={pin} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{pin}</span>
                <span className={`text-sm font-medium ${
                  state === 'HIGH' ? 'text-success-400' : 
                  state.startsWith('PWM') ? 'text-warning-400' : 'text-gray-400'
                }`}>
                  {state}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connection Info */}
      {!isConnected && (
        <div className="card p-4 bg-secondary-500/20 border-secondary-500/30">
          <div className="text-center">
            <WifiOff className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-secondary-300 mb-1">
              Not Connected
            </h4>
            <p className="text-xs text-secondary-400">
              Connect to a device to see status information
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default StatusPanel 